<template>
  <div class="canvas-container"
       @wheel.prevent="handleWheel"
       @mousedown="handleCanvasMouseDown"
       @mousemove="handleMouseMove"
       @mouseup="handleMouseUp"
       @mouseleave="handleMouseUp">

    <!-- 网格背景 -->
    <div class="grid-background"></div>

    <!-- 可缩放平移的画布 -->
    <div class="canvas"
         :style="{
           transform: `scale(${scale}) translate(${offsetX}px, ${offsetY}px)`,
           transformOrigin: '0 0'
         }">

      <!-- 表单模型节点 -->
      <div class="node form-node"
           :style="{left: formNode.x + 'px', top: formNode.y + 'px'}"
           @mousedown.stop="startDragNode($event, 'form')">
        <div class="node-header">
          <div class="node-title">表单模型: {{ form.name }}</div>
          <div class="node-controls">
            <button class="node-collapse-btn" @click.stop="formNode.collapsed = !formNode.collapsed">
              {{ formNode.collapsed ? '+' : '-' }}
            </button>
          </div>
        </div>
        <div class="node-content" v-if="!formNode.collapsed">
          <div v-for="control in controls"
               :key="control.key"
               :data-id="control.key"
               class="node-item"
                :class="{
                 'node-item-selected': isSelected(control),
                 'node-item-binded': isControlBinded(control)
               }">
            <div class="node-item-content">
              <div class="node-item-title">{{ control.name }}</div>
              <div class="node-item-details">
                <span class="node-item-type">{{ control.control }}</span>
                </div>
            </div>
            <div class="connector-point connector-right"
                 @mousedown.stop="startConnection($event, control, 'control')">
            </div>
          </div>
        </div>
    </div>

      <!-- 数据模型节点 -->
      <div class="node data-node"
           :style="{left: dataNode.x + 'px', top: dataNode.y + 'px'}"
           @mousedown.stop="startDragNode($event, 'data')">
        <div class="node-header">
          <div class="node-title">数据模型: {{ model.name }}</div>
          <div class="node-controls">
            <button class="node-collapse-btn" @click.stop="dataNode.collapsed = !dataNode.collapsed">
              {{ dataNode.collapsed ? '+' : '-' }}
            </button>
          </div>
        </div>
        <div class="node-content" v-if="!dataNode.collapsed">
          <div v-for="field in model.fields"
               :key="field.key"
               :data-key="field.key"
               class="node-item"
                :class="{
                 'node-item-selected': isSelected(field),
                 'node-item-readonly': !field.editable,
                 'node-item-binded': isFieldBinded(field)
               }">
            <div class="connector-point connector-left"
                 @mousedown.stop="startConnection($event, field, 'field')">
                </div>
            <div class="node-item-content">
              <div class="node-item-title">{{ field.key }}</div>
              <div class="node-item-details">
                <span class="node-item-type">{{ field.type }}</span>
                <span v-if="!field.editable" class="readonly-badge">只读</span>
                <div class="connector-compatibility"
                    v-if="field.editable && dragging && dragSource && dragSource.type === 'control'">
                  <span v-if="isCompatible(dragSource.item, field)"
                        class="compatibility-indicator compatible">兼容</span>
                  <span v-else class="compatibility-indicator incompatible">不推荐</span>
                </div>
                </div>
        </div>
            <!-- 删除按钮，仅当字段有连接时显示 -->
            <Button
              v-if="isFieldBinded(field)"
              class="delete-connection-btn"
              title="删除连接"
              @click.stop="removeConnectionByField(field)"
              icon="md-close"
            >
            </Button>
            </div>
        </div>
                </div>
            </div>

    <!-- 缩放控制器 -->
    <div class="zoom-controls">
      <button class="zoom-btn" @click="zoomIn">+</button>
      <button class="zoom-btn" @click="zoomOut">-</button>
      <button class="zoom-btn" @click="resetZoom"><BaseIcon icon='md-refresh' /></button>
    </div>

    <!-- 画布信息 -->
    <div class="canvas-info">
      缩放: {{ Math.round(scale * 100) }}% | 连接数: {{ bindingData.length }}</div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

let idGlobal = 1;

export default {
  data() {
    return {
      // 画布状态
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      isDraggingCanvas: false,
      startX: 0,
      startY: 0,

      // 节点状态
      formNode: {
        x: 100,
        y: 100,
        collapsed: false,
        isDragging: false,
      },
      dataNode: {
        x: 500,
        y: 100,
        collapsed: false,
        isDragging: false,
      },

      // 选择状态
      selected: null,

      // 连线相关状态
      bindingData: [],
      dragging: false,
      dragSource: null,
      dragTarget: null,
      mouseX: 0,
      mouseY: 0,
      draftConnectionPath: '',

      // 类型兼容性映射
      compatibilityMap: {
        'string': ['UgoInput', 'UgoTextarea', 'UgoSelect'],
        'number': ['UgoInputNumber', 'UgoSlider'],
        'boolean': ['UgoSwitch', 'UgoCheckbox'],
        'date': ['UgoDatePicker'],
        'array': ['UgoSelect', 'UgoCheckboxGroup', 'UgoMultiForm'],
        'object': ['UgoInnerForm'],
      },

      // 连线层样式
      connectionLayerStyle: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        width: '100%',
        height: '100%',
        transform: 'scale(1) translate(0px, 0px)',
        transformOrigin: '0 0',
      },
      modelKey: null,
    };
  },
  props: {
    model: {
      type: Object,
      default: () => ({
        name: '',
        fields: [],
      }),
    },
    fields: {
      type: Array,
      default: () => ([]),
    },
    form: {
      type: Object,
      default: () => ({
        name: '',
      }),
    },
  },
  watch: {
    'form.model': {
      handler(v) {
        if (v && v !== this.modelKey) {
          this.initializeBindings();
        }
      },
      deep: true,
    },
  },
  computed: {
    controls() {
      const excluded = ['UgoCol', 'UgoRow', 'UgoSection'];
      return this.fields.filter((v) => {
        return !excluded.includes(v.control);
      });
    },
  },
  mounted() {
    this.initializeBindings();
    window.addEventListener('resize', this.updateConnections);

    // 创建线条容器并挂载到body最后
    this.$nextTick(() => {
      this.createConnectionsContainer();
    });
  },
  // 组件激活时显示连线 (用于keep-alive)
  activated() {
    // 组件被重新激活时，重新创建连线容器
    this.$nextTick(() => {
      this.createConnectionsContainer();
    });
  },
  // 组件停用时隐藏连线 (用于keep-alive)
  deactivated() {
    // 组件被停用但未销毁时，移除连线容器
    this.removeConnectionsContainer();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateConnections);

    // 清理线条容器
    this.removeConnectionsContainer();
  },
  destroyed() {
    // 确保组件销毁时连线容器被移除
    console.log('destroy');
    this.removeConnectionsContainer();
  },
  methods: {
    // 创建线条容器的方法
    createConnectionsContainer() {
      // 如果已经存在，先移除
      this.removeConnectionsContainer();

      // 创建容器
      this.connectionsContainer = document.createElement('div');
      this.connectionsContainer.className = 'global-connections-container';
      this.connectionsContainer.setAttribute('data-component-id', this._uid);

      // 设置样式
      Object.assign(this.connectionsContainer.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: '9999',
        overflow: 'visible',
      });

      // 添加到body
      document.body.appendChild(this.connectionsContainer);

      // 创建SVG元素
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.overflow = 'visible'; // 确保SVG内容不被裁剪
      this.connectionsContainer.appendChild(svg);

      // 保存引用
      this.connectionsSvg = svg;

      // 初始更新连线
      this.updateConnections();
    },

    // 移除连线容器的方法
    removeConnectionsContainer() {
      // 确保连线容器完全被移除
      if (this.connectionsContainer) {
        if (document.body.contains(this.connectionsContainer)) {
          document.body.removeChild(this.connectionsContainer);
        }
        this.connectionsContainer = null;
        this.connectionsSvg = null;
      }

      // 移除所有与该组件相关的连线容器(以防有遗漏)
      const orphanContainers = document.querySelectorAll(`.global-connections-container[data-component-id="${this._uid}"]`);
      orphanContainers.forEach((container) => {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      });
    },

    // 画布缩放与平移方法
    handleWheel(event) {
      // 确定鼠标在画布上的相对位置
      const rect = event.currentTarget.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // 计算鼠标在缩放前的画布坐标
      const canvasX = (mouseX - this.offsetX) / this.scale;
      const canvasY = (mouseY - this.offsetY) / this.scale;

      // 根据滚轮方向调整缩放
      let scaleFactor = event.deltaY < 0 ? 1.1 : 0.9;
      let newScale = this.scale * scaleFactor;

      // 限制缩放范围
      newScale = Math.max(0.3, Math.min(2, newScale));
      scaleFactor = newScale / this.scale;

      // 调整偏移量，使鼠标位置保持不变
      this.offsetX = mouseX - canvasX * newScale;
      this.offsetY = mouseY - canvasY * newScale;
      this.scale = newScale;

      // 更新连线层样式与画布同步
      this.updateConnectionLayerStyle();

      // 更新连线
      this.updateConnections();
    },

    zoomIn() {
      if (this.scale < 2) {
        this.scale *= 1.1;
        this.updateConnectionLayerStyle();
        this.updateConnections();
      }
    },

    zoomOut() {
      if (this.scale > 0.3) {
        this.scale *= 0.9;
        this.updateConnectionLayerStyle();
        this.updateConnections();
      }
    },

    resetZoom() {
      this.scale = 1;
      this.offsetX = 0;
      this.offsetY = 0;
      this.updateConnectionLayerStyle();
      this.updateConnections();
    },

    handleCanvasMouseDown(event) {
      // 只有直接点击画布背景时才启动画布拖拽
      if (event.target.classList.contains('canvas-container') ||
          event.target.classList.contains('grid-background') ||
          event.target.classList.contains('canvas')) {
        this.isDraggingCanvas = true;
        this.startX = event.clientX - this.offsetX;
        this.startY = event.clientY - this.offsetY;
        event.preventDefault();
      }
    },

    // 节点拖拽方法
    startDragNode(event, nodeType) {
      const node = nodeType === 'form' ? this.formNode : this.dataNode;
      node.isDragging = true;
      node.startX = event.clientX / this.scale - node.x;
      node.startY = event.clientY / this.scale - node.y;
      event.preventDefault();
    },

    // 鼠标移动处理
    handleMouseMove(event) {
      // 处理画布拖拽
      if (this.isDraggingCanvas) {
        this.offsetX = event.clientX - this.startX;
        this.offsetY = event.clientY - this.startY;

        // 画布拖拽时也需要更新连线
        this.updateConnections();
        return;
      }

      // 处理节点拖拽
      if (this.formNode.isDragging) {
        this.formNode.x = event.clientX / this.scale - this.formNode.startX;
        this.formNode.y = event.clientY / this.scale - this.formNode.startY;
        this.updateConnections();
        return;
      }

      if (this.dataNode.isDragging) {
        this.dataNode.x = event.clientX / this.scale - this.dataNode.startX;
        this.dataNode.y = event.clientY / this.scale - this.dataNode.startY;
        this.updateConnections();
        return;
      }

      // 处理连线拖拽
      if (this.dragging) {
        this.mouseX = event.clientX;
        this.mouseY = event.clientY;
        this.updateDraftConnection();
      }
    },

    // 鼠标释放处理
    handleMouseUp(event) {
      // 重置画布拖拽状态
      this.isDraggingCanvas = false;

      // 重置节点拖拽状态
      this.formNode.isDragging = false;
      this.dataNode.isDragging = false;

      // 处理连线完成
      if (this.dragging) {
        const target = this.findDropTarget(event.target);

        if (target) {
          // 如果源和目标类型不同，则创建连接
          const sourceType = this.dragSource.type;
          const targetType = target.type;

          if (sourceType !== targetType) {
            const field = sourceType === 'field' ? this.dragSource.item : target.item;
            const control = sourceType === 'control' ? this.dragSource.item : target.item;

            // 移除严格兼容性检查，只要字段可编辑就允许连接
            if (field.editable) {
              this.createConnection(field, control);
            }
          }
        }

        // 重置拖拽状态
        this.dragging = false;
        this.dragSource = null;
        this.draftConnectionPath = '';

        // 立即更新连线，确保草稿线被移除
        this.updateConnections();
      }
    },

    // 数据绑定相关方法
    createID() {
      const time = Date.now();
      return 'field-' + time.toString(16) + idGlobal++;
    },

    initializeBindings() {
      this.modelKey = this.form.model;
      // 初始化绑定数据
      this.bindingData = [];
      console.log(this.form);
      if (this.form.model_binding && Array.isArray(this.form.model_binding.data) && this.form.model_binding.data.length > 0) {
        this.bindingData = cloneDeep(this.form.model_binding.data || []);
      } else {
        this.controls.forEach((control) => {
          const matchingField = this.model.fields.find((f) => f.key === control.key);
          if (matchingField) {
            this.bindingData.push({
              fieldKey: matchingField.key,
              controlId: control.key,
              fieldType: matchingField.type,
              controlType: control.control,
            });
          }
        });

        this.saveBindings();
      }

      // 在下一个DOM更新循环后绘制连线
      this.$nextTick(() => {
        this.updateConnections();
      });
    },

    isControlBinded(control) {
      return this.bindingData.some((binding) => binding.controlId === control.key);
    },

    isFieldBinded(field) {
      return this.bindingData.some((binding) => binding.fieldKey === field.key);
    },

    isSelected(item) {
      return this.selected && (
        (item.key && this.selected.key === item.key)
      );
    },

    isCompatible(control, field) {
      if (!field || !field.type || !control) return false;

      const compatibleControls = this.compatibilityMap[field.type] || [];
      return compatibleControls.includes(control.control);
    },

    // 连线相关方法
    startConnection(event, item, type) {
      // 如果是只读字段，不允许连线
      if (type === 'field' && !item.editable) return;

      this.dragging = true;
      this.dragSource = {item, type};

      // 获取连接点元素
      const connectorEl = event.target;
      // 获取元素相对于页面的确切坐标
      const connectorRect = connectorEl.getBoundingClientRect();

      // 直接使用页面坐标，因为我们将在绝对定位的SVG中绘制
      const x = connectorRect.left + connectorRect.width/2;
      const y = connectorRect.top + connectorRect.height/2;

      this.dragSource.x = x;
      this.dragSource.y = y;

      this.mouseX = event.clientX;
      this.mouseY = event.clientY;

      this.updateDraftConnection();

      event.preventDefault();
      event.stopPropagation();
    },

    updateDraftConnection() {
      if (!this.dragging || !this.dragSource) return;

      // 创建贝塞尔曲线
      let x1; let y1; let x2; let y2;

      // 获取连接点
      if (this.dragSource.type === 'control') {
        const connectorEl = this.$el.querySelector(`.node-item[data-id="${this.dragSource.item.key}"] .connector-right`);
        if (connectorEl) {
          const rect = connectorEl.getBoundingClientRect();
          x1 = rect.left + rect.width / 2;
          y1 = rect.top + rect.height / 2;
        } else {
          x1 = this.dragSource.x;
          y1 = this.dragSource.y;
        }
      } else {
        const connectorEl = this.$el.querySelector(`.node-item[data-key="${this.dragSource.item.key}"] .connector-left`);
        if (connectorEl) {
          const rect = connectorEl.getBoundingClientRect();
          x1 = rect.left + rect.width / 2;
          y1 = rect.top + rect.height / 2;
        } else {
          x1 = this.dragSource.x;
          y1 = this.dragSource.y;
        }
      }

      // 鼠标位置作为终点
      x2 = this.mouseX;
      y2 = this.mouseY;

      // 计算控制点
      const dx = Math.abs(x2 - x1) * 0.5;

      // 根据拖拽源类型调整曲线
      if (this.dragSource.type === 'control') {
        this.draftConnectionPath = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
      } else {
        this.draftConnectionPath = `M ${x1} ${y1} C ${x1 - dx} ${y1}, ${x2 + dx} ${y2}, ${x2} ${y2}`;
      }

      // 更新连线
      this.updateConnections();
    },

    findDropTarget(element) {
      // 从事件目标向上查找连接点
      let current = element;
      while (current && !current.classList.contains('connector-point')) {
        current = current.parentElement;
        if (!current || current.classList.contains('canvas-container')) break;
      }

      if (!current || !current.classList.contains('connector-point')) return null;

      // 判断连接点类型
      const isLeftConnector = current.classList.contains('connector-left');
      const isRightConnector = current.classList.contains('connector-right');

      if (!isLeftConnector && !isRightConnector) return null;

      // 找到所属的节点项
      let nodeItem = current.parentElement;
      while (nodeItem && !nodeItem.classList.contains('node-item')) {
        nodeItem = nodeItem.parentElement;
      }

      if (!nodeItem) return null;

      // 提取key或id
      const key = nodeItem.getAttribute('data-key');
      const control_key = nodeItem.getAttribute('data-id');

      // 确定是field还是control
      let type; let item;
      if (isLeftConnector) {
        type = 'field';
        item = this.model.fields.find((f) => f.key === key);
      } else {
        type = 'control';
        item = this.controls.find((c) => c.key === control_key);
      }

      if (!item) return null;

      // 计算连接点位置
      const canvas = document.querySelector('.canvas');
      const canvasRect = canvas.getBoundingClientRect();
      const rect = current.getBoundingClientRect();

      // 转换为画布坐标
      const x = (rect.left + rect.width / 2 - canvasRect.left) / this.scale - this.offsetX;
      const y = (rect.top + rect.height / 2 - canvasRect.top) / this.scale - this.offsetY;

      return {
        type,
        item,
        x,
        y,
      };
    },

    createConnection(field, control) {
      // 移除已有的连接
      this.bindingData = this.bindingData.filter((b) =>
        b.fieldKey !== field.key && b.controlId !== control.key,
      );

      // 添加新的连接
      this.bindingData.push({
        fieldKey: field.key,
        controlId: control.key,
        fieldType: field.type,
        controlType: control.control,
      });

      // 更新model数据
      this.saveBindings();

      // 更新连线显示
      this.$nextTick(() => {
        this.updateConnections();
      });
    },

    removeConnection(binding) {
      // 双击连线时删除连接
      this.bindingData = this.bindingData.filter((b) =>
        !(b.fieldKey === binding.fieldKey && b.controlId === binding.controlId),
      );

      // 更新model数据
      this.saveBindings();

      // 更新连线
      this.$nextTick(() => this.updateConnections());
    },

    getConnectionPath(binding) {
      // 查找对应的控件和字段元素
      const control = this.controls.find((c) => c.key === binding.controlId);
      const field = this.model.fields.find((f) => f.key === binding.fieldKey);

      if (!control || !field) return '';

      // 获取控件和字段在各自数组中的索引
      const controlIndex = this.controls.findIndex((c) => c.key === control.key);
      const fieldIndex = this.model.fields.findIndex((f) => f.key === field.key);

      if (controlIndex === -1 || fieldIndex === -1) return '';

      // 获取连接点DOM元素
      const controlConnector = this.$el.querySelector(`.node-item[data-id="${control.key}"] .connector-right`);
      const fieldConnector = this.$el.querySelector(`.node-item[data-key="${field.key}"] .connector-left`);

      if (!controlConnector || !fieldConnector) {
        // 如果DOM元素不存在，回退到计算方式
        return this.getConnectionPathByCalculation(control, field, controlIndex, fieldIndex);
      }

      // 获取连接点的精确页面坐标
      const controlRect = controlConnector.getBoundingClientRect();
      const fieldRect = fieldConnector.getBoundingClientRect();

      // 连接点中心坐标
      const x1 = controlRect.left + controlRect.width / 2;
      const y1 = controlRect.top + controlRect.height / 2;
      const x2 = fieldRect.left + fieldRect.width / 2;
      const y2 = fieldRect.top + fieldRect.height / 2;

      // 创建贝塞尔曲线
      const dx = Math.abs(x2 - x1) * 0.5;
      return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
    },

    // 当DOM元素不可用时的备用计算方法
    getConnectionPathByCalculation(control, field, controlIndex, fieldIndex) {
      // 获取canvas容器位置
      const containerRect = this.$el.getBoundingClientRect();

      // 计算连接点在页面上的绝对位置
      // 控件连接点 (表单模型右侧)
      const x1 = (this.formNode.x + 250) * this.scale + containerRect.left; // 右侧连接点
      const y1 = (this.formNode.y + 40 + (40 * controlIndex) + 20) * this.scale + containerRect.top; // 垂直中点

      // 字段连接点 (数据模型左侧)
      const x2 = this.dataNode.x * this.scale + containerRect.left; // 左侧连接点
      const y2 = (this.dataNode.y + 40 + (40 * fieldIndex) + 20) * this.scale + containerRect.top; // 垂直中点

      // 创建贝塞尔曲线
      const dx = Math.abs(x2 - x1) * 0.5;
      return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
    },

    updateConnections() {
      // 确保SVG容器存在
      if (!this.connectionsSvg) return;

      // 检查组件是否还在DOM中
      if (!document.body.contains(this.$el)) {
        this.removeConnectionsContainer();
        return;
      }

      // 请求动画帧以减少不必要的渲染
      requestAnimationFrame(() => {
        // 清空现有连线
        while (this.connectionsSvg.firstChild) {
          this.connectionsSvg.removeChild(this.connectionsSvg.firstChild);
        }

        // 绘制所有绑定连线
        this.bindingData.forEach((binding) => {
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          const pathData = this.getConnectionPath(binding);
          if (!pathData) return;

          path.setAttribute('d', pathData);
          path.setAttribute('class', 'connection-path');
          path.setAttribute('stroke', 'var(--success, #10b981)');
          path.setAttribute('stroke-width', '2');
          path.setAttribute('fill', 'none');

          // 设置为可互动
          path.style.pointerEvents = 'stroke';
          path.style.cursor = 'pointer';

          // 设置悬停效果
          path.addEventListener('mouseenter', () => {
            path.setAttribute('stroke-width', '3');
            path.setAttribute('stroke', 'var(--primary, #3b82f6)');
          });

          path.addEventListener('mouseleave', () => {
            path.setAttribute('stroke-width', '2');
            path.setAttribute('stroke', 'var(--success, #10b981)');
          });

          // 双击删除功能
          path.addEventListener('dblclick', (e) => {
            e.stopPropagation();
            this.removeConnection(binding);
          });

          // 添加到SVG
          this.connectionsSvg.appendChild(path);
        });

        // 如果正在拖拽，添加草稿线
        if (this.dragging && this.draftConnectionPath) {
          const draftPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          draftPath.setAttribute('d', this.draftConnectionPath);
          draftPath.setAttribute('class', 'connection-path-draft');
          draftPath.setAttribute('stroke', 'var(--primary, #3b82f6)');
          draftPath.setAttribute('stroke-width', '2');
          draftPath.setAttribute('stroke-dasharray', '5,5');
          draftPath.setAttribute('fill', 'none');

          this.connectionsSvg.appendChild(draftPath);
        }
      });
    },

    saveBindings() {
      // 发送保存事件并传递绑定数据
      this.$emit('update', this.bindingData);
    },

    removeConnectionByField(field) {
      // 根据字段删除连接
      this.bindingData = this.bindingData.filter((b) => b.fieldKey !== field.key);
      // 更新连线
      this.updateConnections();

      // 更新model数据
      this.saveBindings();
    },

    // 新方法：更新连线层样式，使其与画布同步
    updateConnectionLayerStyle() {
      // 获取canvas容器的位置
      const containerRect = this.$el.getBoundingClientRect();

      // 计算连接层的绝对位置
      this.connectionLayerStyle = {
        position: 'fixed',
        top: containerRect.top + 'px',
        left: containerRect.left + 'px',
        width: containerRect.width + 'px',
        height: containerRect.height + 'px',
        transform: `scale(${this.scale}) translate(${this.offsetX}px, ${this.offsetY}px)`,
        transformOrigin: '0 0',
      };
    },
  },
};
</script>

<style lang="less" scoped>
.canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #f9f9f9;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
}

/* 网格背景 */
.grid-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  background-image:
    linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px);
  background-size: 20px 20px;
}

/* 画布 */
.canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: 0 0;
}

/* 节点样式 */
.node {
  position: absolute;
  width: 250px;
  border-radius: 6px;
  background-color: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: visible;
  cursor: move;
  z-index: 1;
  font-size: 12px;
}

.form-node {
  border-top: 3px solid var(--primary);
}

.data-node {
  border-top: 3px solid var(--success);
}

.node-header {
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fafbfc;
  border-bottom: 1px solid var(--border);
}

.node-title {
  font-weight: bold;
  font-size: 14px;
  color: var(--text);
}

.node-controls {
  display: flex;
  gap: 5px;
}

.node-collapse-btn {
  width: 20px;
  height: 20px;
    border-radius: 3px;
  border: 1px solid var(--border);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}

.node-content {
  height: auto;
  overflow: visible;
}

.node-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
  cursor: default;
  transition: background-color 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: rgba(0,0,0,0.02);
  }

  &.node-item-selected {
    background-color: rgba(var(--primary-rgb), 0.1);
  }

  &.node-item-binded .connector-point {
    background-color: var(--success);
  }

  &.node-item-readonly {
    opacity: 0.7;
  }
}

.node-item-content {
  flex: 1;
  min-width: 0;
  margin: 0 10px 0 15px;
  overflow: hidden;
}

.node-item-title {
  font-weight: 500;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.node-item-details {
  display: flex;
  align-items: center;
  color: var(--subtext2);
  gap: 6px;
  font-size: 11px;
  overflow: hidden;
  width: 100%;
}

.node-item-type {
  padding: 1px 4px;
  background-color: rgba(0,0,0,0.05);
  border-radius: 3px;
}

.readonly-badge {
  font-size: 10px;
  color: var(--error);
  background-color: rgba(var(--error-rgb), 0.1);
  padding: 1px 4px;
  border-radius: 3px;
}

/* 连接点 */
.connector-point {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--border);
  cursor: crosshair;
  z-index: 3;
  transition: all 0.2s ease;
  position: relative;

  &:hover, &.active {
    transform: scale(1.3);
    background-color: var(--primary);
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 4px;
    height: 4px;
    background-color: white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }
}

.connector-left {
  margin-left: -5px;
}

.connector-right {
  margin-right: -5px;
}

/* 兼容性指示器 */
.compatibility-indicator {
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
}

.compatibility-indicator.compatible {
  color: var(--success);
  background-color: rgba(var(--success-rgb), 0.1);
}

.compatibility-indicator.incompatible {
  color: var(--error);
  background-color: rgba(var(--error-rgb), 0.1);
}

/* 缩放控制 */
.zoom-controls {
  position: absolute;
  bottom: 20px;
  left: 20px;
  display: flex;
  gap: 5px;
  z-index: 10;
}

.zoom-btn {
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background-color: white;
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);

  &:hover {
    background-color: #f0f0f0;
  }
}

/* 保存按钮 */
.floating-save-btn {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--primary);
  color: white;
  border: none;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: transform 0.2s, background-color 0.2s;

  &:hover {
    transform: scale(1.1);
    background-color: var(--primary-dark, #0056b3);
    opacity: 0.9;
  }
}

.save-icon {
  font-size: 18px;
}

/* 画布信息 */
.canvas-info {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(255,255,255,0.8);
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  color: var(--subtext);
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  pointer-events: none;
  z-index: 10;
}

/* 删除连接按钮 */
.delete-connection-btn {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--error);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-left: 5px;
  font-size: 14px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }

  span {
    line-height: 1;
    font-weight: bold;
  }
}
</style>
